import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom-v5-compat";
import { HOTEL_URL_PARAM_KEYS } from "@b2bportal/core-utilities";
import type { Tile } from "@b2bportal/rbc-deal-tiles-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";
import { DealTile } from "../DealTile";
import { getOfferImageSrc } from "@/modules/hotel-funded-offers/offerImage";
import {
  getDefaultOfferStayDates,
  OFFER_SEARCH_ADULTS,
  OFFER_SEARCH_ROOMS,
} from "@/modules/hotel-funded-offers/offerSearch";
import {
  ClaimStatus,
  HOTEL_DEALS_OFFER_ID,
  HOTEL_DEALS_OFFER_NAME,
  IsOfferClaimed,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";
import { HOTEL_FUNDED_OFFERS_PATH } from "@/utilities";

export interface HotelDealTileProps {
  tile: Tile;
  imageUrl: string;
  /** Position of this tile in the Hotel deals section (0-based). */
  tileIndex: number;
  screen?: OfferScreen;
  className?: string;
}

export const HotelDealTile = ({
  tile,
  imageUrl,
  tileIndex,
  screen = OfferScreen.home,
  className,
}: HotelDealTileProps) => {
  const { t, locale } = useI18nContext();
  const navigate = useNavigate();

  const percent = getPercentage(tile.savingsPercentage * 100, locale);
  const title = t("hotelDealsSection.tileTitle", {
    percent,
    destination: tile.label,
  });

  const imageSrc = getOfferImageSrc(imageUrl);

  const baseTileProperties = {
    offer_type: OfferType.hotel_merch,
    offer_id: HOTEL_DEALS_OFFER_ID,
    offer_name: HOTEL_DEALS_OFFER_NAME,
    destination: tile.market?.marketId ?? "",
    destination_label: tile.label,
    destination_country_code: tile.market?.countryCode ?? "",
    tile_index: tileIndex,
    discount_percentage: tile.savingsPercentage * 100,
    screen,
    is_claimable: false,
  };

  const emitTappedTile = useEmitRbcRawEvent(OfferRawEventName.TappedOfferTile);

  const { ref, inView } = useInView({
    threshold: [0.5],
    delay: 1000,
    triggerOnce: true,
  });
  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.ViewedOfferTile,
      properties: {
        ...baseTileProperties,
        is_offer_claimed: IsOfferClaimed.not_applicable,
      },
    },
    inView
  );

  // "Book" and the card open the destination's funded offers page, which
  // resolves the market's lodging IDs and shows the offer terms.
  const goToOffers = () => {
    emitTappedTile({
      properties: {
        ...baseTileProperties,
        claim_status: ClaimStatus.not_applicable,
      },
    });
    if (!tile.market) {
      return;
    }
    const { marketId, countryCode } = tile.market;

    const { fromDate, untilDate } = getDefaultOfferStayDates();
    const params = new URLSearchParams({
      marketId,
      countryCode,
      [HOTEL_URL_PARAM_KEYS.FROM_DATE]: fromDate,
      [HOTEL_URL_PARAM_KEYS.UNTIL_DATE]: untilDate,
      [HOTEL_URL_PARAM_KEYS.ADULTS_COUNT]: String(OFFER_SEARCH_ADULTS),
      [HOTEL_URL_PARAM_KEYS.ROOMS_COUNT]: String(OFFER_SEARCH_ROOMS),
    });
    navigate(`${HOTEL_FUNDED_OFFERS_PATH}?${params.toString()}`, {
      state: { tile },
    });
  };

  return (
    <DealTile
      rootRef={ref}
      className={className}
      imageSrc={imageSrc}
      imageAlt={tile.label}
      title={title}
      description={t("hotelDealsSection.description")}
      action={{ label: t("dealTile.book"), onClick: goToOffers }}
      onCardClick={goToOffers}
    />
  );
};
